import { FormControl, FormErrorMessage } from "@chakra-ui/form-control";
import { Box, Button, Checkbox, Flex, Input, Text, useToast, useTranslation, Link, Icon } from "@familyzone/component-library";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { capitalise } from "../../helpers/stringHelpers";
import { useSchoolCalendarStore } from "../../storez/SchoolCalendarStore";
import { Link as ReactLink } from "react-router";
import { SchoolCalendarRule, SchoolTimePlaceholder, Weekday, WeekdayFullNames, WEEKDAYS } from "../../types/Calendar";
import { convertToIntegerTime, convertToPrintableTime } from "../../utils/DateTimeUtil";
import SchoolCalendarSetupModal from "../modals/SchoolCalendarSetupModal";
import CardBasedPage from "../templates/CardBasedPage";
import NonSchoolDays from "./Calendar/NonSchoolDays";
import { useDevicePermissionsStore } from "../../storez/DevicePermissionsStore";
import { isCommunityAuditsAccessible } from "../debug/ConfigAudit/AuditUtils";
import { AuditScope } from "../../types/Audit";
import SessionStore from "../../stores/SessionStore";
import SaveConflictErrorModal from "../modals/SaveConflictErrorModal";
import { ResponseError } from "../../types/Api";

type InputTimes = Record<Weekday, { startTime: string; endTime: string }>;
type InputErrors = Record<Weekday, { startTime?: string; endTime?: string }>;

const SchoolCalendar: React.FC = () => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const title = t("School Calendar");
  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("School Calendar"), isActive: true },
  ];

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showSetupModal, setShowSetupModal] = useState<boolean>(false);
  const [showConflictModal, setShowConflictModal] = useState<boolean>(false);

  const [inputErrors, setInputErrors] = useState<InputErrors>(makeInitialInputErrors);
  const [schoolTimes, setSchoolTimes] = useState<SchoolCalendarRule[]>([]);
  const [inputTimes, setInputTimes] = useState<InputTimes>(makeInitialInputTimes);

  const [storedSchoolTimes, fetchSchoolTime, saveBatchSchoolTime, resetStore] = useSchoolCalendarStore(
    useCallback((state) => [state.schoolTimeRules, state.fetch, state.saveBatchSchoolDays, state.reset] as const, [])
  );

  const [permissions] = useDevicePermissionsStore(useCallback((state) => [state.permissions] as const, []));

  const showFetchErrorToast = () => {
    errorToast({
      title: t("Please try again"),
      description: t("Failed to load calendar"),
      isClosable: true,
    });
  };

  const showSaveSuccessToast = () => {
    successToast({
      title: t("School time and days saved"),
      description: t("School time and days have been set up successfully in the school calendar."),
      isClosable: true,
    });
  };

  const showSaveErrorToast = () => {
    errorToast({
      title: t("Please try again"),
      description: t("An unexpected error occurred while saving the calendar"),
      isClosable: true,
    });
  };

  useEffect(() => {
    fetchSchoolTime().then((rules) => {
      const setupModalSeen = sessionStorage.getItem("cal-setup-seen");
      const noRulesExist = !rules || rules.length === 0;

      if (!setupModalSeen && noRulesExist) {
        setShowSetupModal(true);
      }

      if (noRulesExist) {
        setSchoolTimes(makeDefaultSchoolTimes());
      }
      setLoading(false);
    }, showFetchErrorToast);

    return resetStore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSchoolTime]);

  useEffect(() => {
    if (storedSchoolTimes && storedSchoolTimes.length > 0) {
      setSchoolTimes(storedSchoolTimes);

      // Load in the inputs the times that come from backend.
      // Create a Map, for convenience, with the day as key, e.g: "mon".
      const storedSchoolTimesMap = new Map(storedSchoolTimes.map((rule) => [rule.recurrence.days[0], rule]));
      const newInputTimes: Partial<InputTimes> = {};
      for (const day of WEEKDAYS) {
        const match = storedSchoolTimesMap.get(day);
        if (!match) continue;
        newInputTimes[day] = {
          startTime: convertToPrintableTime(match.startTime),
          endTime: convertToPrintableTime(match.endTime),
        };
      }

      // Here we merge the input times that come from backend with the ones
      // already in the UI: these are the default ones, or the ones set by
      // the user while modifying the different days. If the user typed 9:30
      // we change the times to 09:30, to match the data that comes from backend.
      // If is not valid we set it to the default one.
      setInputTimes((prev) => {
        const formatted = Object.entries(prev).map(([day, { startTime, endTime }]) => [
          day,
          {
            startTime: formatInput(startTime, SchoolTimePlaceholder.startTime),
            endTime: formatInput(endTime, SchoolTimePlaceholder.endTime),
          },
        ]);

        return { ...(Object.fromEntries(formatted) as InputTimes), ...newInputTimes };
      });
      setLoading(false);
    } else if (sessionStorage.getItem("cal-setup-seen")) {
      setInputTimes((prev) => prev ?? makeInitialInputTimes());
      setLoading(false);
    }
  }, [storedSchoolTimes]);

  const isValidTime = (time: number): boolean => {
    const minutes = time % 100;
    const hour = (time - minutes) / 100;
    return hour >= 0 && hour <= 23 && minutes >= 0 && minutes <= 59;
  };

  const validateRule = (startTime: number, endTime: number): { startTime?: string; endTime?: string } => {
    const errors: { startTime?: string; endTime?: string } = {};

    if (startTime > -1) {
      if (!isValidTime(startTime)) {
        errors.startTime = t("Start time must be in the range of 00:00 and 23:59");
      }
    } else {
      errors.startTime = t("Start time must be specified in format HH:MM");
    }

    if (endTime > -1) {
      if (!isValidTime(endTime)) {
        errors.endTime = t("End time must be in the range of 00:00 and 23:59");
      } else if (isValidTime(startTime) && endTime <= startTime) {
        errors.endTime = t("End time must be after start time");
      }
    } else {
      if (startTime === -1) {
        errors.startTime = t("Start and end time must be specified in format HH:MM");
      } else {
        errors.endTime = t("End time must be specified in format HH:MM");
      }
    }

    return errors;
  };

  const areInputsValid = (): boolean => {
    const errors = makeInitialInputErrors();
    for (const { startTime, endTime, recurrence } of schoolTimes) {
      const day = recurrence.days[0];
      errors[day] = validateRule(startTime, endTime);
    }
    setInputErrors(errors);
    return Object.values(errors).every(({ endTime, startTime }) => !endTime && !startTime);
  };

  const handleChangeStartTime = (event: React.ChangeEvent<HTMLInputElement>, weekday: Weekday) => {
    const startTime = event.target.value;
    setInputTimes((prev) => ({ ...prev, [weekday]: { ...prev[weekday], startTime } }));
    setSchoolTimes((prev) =>
      prev.map((rule) => (rule.recurrence?.days[0] === weekday ? { ...rule, startTime: convertToIntegerTime(startTime) } : rule))
    );
  };

  const handleChangeEndTime = (event: React.ChangeEvent<HTMLInputElement>, weekday: Weekday) => {
    const endTime = event.target.value;
    setInputTimes((prev) => ({ ...prev, [weekday]: { ...prev[weekday], endTime } }));
    setSchoolTimes((prev) =>
      prev.map((rule) => (rule.recurrence?.days[0] === weekday ? { ...rule, endTime: convertToIntegerTime(endTime) } : rule))
    );
  };

  const handleCheckDay = (event: React.ChangeEvent<HTMLInputElement>, weekday: Weekday) => {
    if (!event.target.checked) {
      // Do not allow to uncheck if there is only 1 checked.
      if (schoolTimes.length === 1) return;
      setSchoolTimes((prev) => prev.filter((rule) => rule.recurrence.days[0] !== weekday));
      return;
    }

    const { startTime, endTime } = inputTimes[weekday];

    // Try to restore the original rule, to keep id and version.
    const original = storedSchoolTimes?.find((r) => r.recurrence?.days?.[0] === weekday);

    setSchoolTimes((prev) => [
      ...prev,
      {
        ...(original ?? SchoolTimePlaceholder),
        startTime: convertToIntegerTime(startTime),
        endTime: convertToIntegerTime(endTime),
        id: original?.id ?? undefined,
        version: original?.version ?? 1,
        recurrence: { ...SchoolTimePlaceholder.recurrence, days: [weekday] },
      },
    ]);
  };

  const handleSave = () => {
    if (!areInputsValid()) return;
    setSaving(true);
    saveBatchSchoolTime(schoolTimes)
      .then(showSaveSuccessToast, (err: ResponseError) => {
        if (err.status === 409) {
          setShowConflictModal(true);
        } else {
          showSaveErrorToast();
        }
      })
      .finally(() => setSaving(false));
  };

  const handleCloseSetupModal = (): void => {
    sessionStorage.setItem("cal-setup-seen", "true");
    setLoading(false);
    setShowSetupModal(false);
  };

  const handleCloseConflictModal = (): void => {
    setShowConflictModal(false);
  };

  const handleConfirmSave = (): void => {
    setSaving(true);
    saveBatchSchoolTime(schoolTimes, { fixVersionConflict: true })
      .then(showSaveSuccessToast, showSaveErrorToast)
      .finally(() => {
        setSaving(false);
        setShowConflictModal(false);
      });
  };

  const handleReloadPage = (): void => {
    setLoading(true);
    fetchSchoolTime().then(() => {
      setShowConflictModal(false);
    }, showFetchErrorToast);
  };

  const isDayChecked = (day: Weekday) => {
    return schoolTimes && !!schoolTimes.find((rule) => rule.recurrence?.days?.[0] === day);
  };

  const DayCheckBox: React.FC<{ day: Weekday }> = ({ day }) => {
    return (
      <Box paddingTop="4">
        <Checkbox isChecked={isDayChecked(day)} onChange={(e) => handleCheckDay(e, day)} name={`school-day-${day}`}>
          {capitalise(WeekdayFullNames[day])}
        </Checkbox>
      </Box>
    );
  };

  const hasChanges = () => {
    // If the user unchecked all days don't allow to click
    if (schoolTimes.length === 0) {
      return false;
    }
    // If the number of checked days is different than the
    // number of rules that came from the backend we know we
    // we have changes. This can be the onboarding scenario:
    // 0 from backend, multiple selected by default.
    if (!loading && schoolTimes.length !== storedSchoolTimes.length) {
      return true;
    }

    // Create a map with the stored school times for convenience.
    const storedRuleMap = new Map(
      storedSchoolTimes.map((rule) => [rule.recurrence.days[0], { startTime: rule.startTime, endTime: rule.endTime }])
    );

    return schoolTimes.some((rule) => {
      const day = rule.recurrence.days[0];
      const storedRule = storedRuleMap.get(day);
      // If the user adds a new day we have changes.
      if (!storedRule) return true;
      return rule.startTime !== storedRule.startTime || rule.endTime !== storedRule.endTime;
    });
  };

  // eslint-disable-next-line
  const isSupportAdmin = SessionStore.isSupportAdmin() as boolean;

  const canAccessCommunity = useMemo(() => {
    return isCommunityAuditsAccessible(permissions, isSupportAdmin);
  }, [permissions, isSupportAdmin]);

  const viewHistoryLink = canAccessCommunity ? (
    <>
      <Box textAlign={"right"} lineHeight="31px">
        <Link className="personaModule-rightLink" as={ReactLink} to={"/managedevice/settings/audit/community?scope=" + AuditScope.Calendar}>
          {t("View History")}
        </Link>
      </Box>
    </>
  ) : undefined;

  const isFormInvalid = (weekday: Weekday) => {
    const { endTime, startTime } = inputErrors[weekday];
    return Boolean(endTime || startTime);
  };

  const handleCopyToAll = () => {
    const firstCheckedDay = Object.keys(inputTimes).find((day) => isDayChecked(day as Weekday)) as Weekday | undefined;
    if (!firstCheckedDay) return;
    const times = inputTimes[firstCheckedDay];
    const errors = validateRule(convertToIntegerTime(times.startTime), convertToIntegerTime(times.endTime));
    const isValid = !errors.startTime && !errors.endTime;
    if (!isValid) {
      setInputErrors({ ...makeInitialInputErrors(), [firstCheckedDay]: errors });
      return;
    }

    const startTime = convertToIntegerTime(times.startTime);
    const endTime = convertToIntegerTime(times.endTime);

    setSchoolTimes((prev) => prev.map((rule) => ({ ...rule, startTime, endTime })));
    setInputTimes((prev) => {
      if (!prev?.[firstCheckedDay]) return prev;
      return Object.fromEntries(Object.keys(prev).map((day) => [day, { ...prev[firstCheckedDay] }])) as InputTimes;
    });
    setInputErrors(makeInitialInputErrors());
  };

  return (
    <>
      <Box flexDirection="column" minWidth="100%">
        <CardBasedPage title={title} tools={viewHistoryLink} breadcrumbs={breadcrumbs}>
          <Box p="sp24" color="neutrals.900">
            <Text fontFamily="Poppins" fontWeight="medium" fontSize="large">
              {t("School Time & Days")}
            </Text>
          </Box>
          <Box px="sp24" color="neutrals.900">
            {t("Set school days and hours. This defines when reporting and parent features apply.")}
          </Box>
          <Box p="sp24" mb="sp32">
            <Flex gap="16" flexDirection="column">
              {Object.values(WEEKDAYS).map((weekday, index) => (
                <FormControl isDisabled={loading || saving} isInvalid={isFormInvalid(weekday)} key={`key-${weekday}`}>
                  <Flex alignItems="start">
                    <Flex width="36" minWidth="36" alignItems="center" mt="5">
                      <DayCheckBox day={weekday} />
                    </Flex>
                    <Flex flexDirection="column">
                      <Flex>
                        <Input
                          py="sp8"
                          w="86px"
                          value={inputTimes[weekday].startTime}
                          onChange={(e) => handleChangeStartTime(e, weekday)}
                          aria-label={`${capitalise(WeekdayFullNames[weekday])} Start Time`}
                          isDisabled={!isDayChecked(weekday)}
                        />
                        <Text
                          display="flex"
                          alignItems="center"
                          px="sp8"
                          textColor={isDayChecked(weekday) ? undefined : "GrayText"}
                          transition="color 0.1s ease-in-out"
                        >
                          {t("to")}
                        </Text>
                        <Input
                          py="sp8"
                          w="86px"
                          value={inputTimes[weekday].endTime}
                          onChange={(e) => handleChangeEndTime(e, weekday)}
                          aria-label={`${capitalise(WeekdayFullNames[weekday])} End Time`}
                          isDisabled={!isDayChecked(weekday)}
                        />
                        {index === 0 ? (
                          <Button cursor="pointer" ml="20" variant="ghost" paddingY="2" paddingX="8" onClick={handleCopyToAll}>
                            <Flex alignItems="center">
                              <Icon icon="far fa-copy" color="brand.400" />
                              <Text pl="sp8" fontWeight="bold" color="brand.400">
                                {t("Copy to all")}
                              </Text>
                            </Flex>
                          </Button>
                        ) : null}
                      </Flex>
                      <FormErrorMessage
                        py="sp8"
                        fontWeight="regular"
                        flexDirection="column"
                        alignItems="start"
                        data-testid={`${weekday}-errors`}
                      >
                        {inputErrors[weekday].startTime && <Text>{inputErrors[weekday].startTime}</Text>}
                        {inputErrors[weekday].endTime && <Text>{inputErrors[weekday].endTime}</Text>}
                      </FormErrorMessage>
                    </Flex>
                  </Flex>
                </FormControl>
              ))}
            </Flex>
          </Box>
          <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
            <Button variant="primary" isDisabled={loading || saving || !hasChanges()} onClick={handleSave}>
              Save School Time & Days
            </Button>
          </Box>
        </CardBasedPage>
        <NonSchoolDays nonSchoolDaysEnabled={storedSchoolTimes && storedSchoolTimes.length > 0} />
      </Box>
      <SchoolCalendarSetupModal open={showSetupModal} onClose={handleCloseSetupModal} />
      <SaveConflictErrorModal
        open={showConflictModal}
        onClose={handleCloseConflictModal}
        onSave={handleConfirmSave}
        onReload={handleReloadPage}
      />
    </>
  );
};

function makeInitialInputTimes(): InputTimes {
  const times = {} as InputTimes;
  for (const d of WEEKDAYS) {
    times[d] = {
      startTime: convertToPrintableTime(SchoolTimePlaceholder.startTime),
      endTime: convertToPrintableTime(SchoolTimePlaceholder.endTime),
    };
  }
  return times;
}

function makeInitialInputErrors(): InputErrors {
  const errs = {} as InputErrors;
  for (const d of WEEKDAYS) {
    errs[d] = {};
  }
  return errs;
}

// Default rules for Monday to Friday
function makeDefaultSchoolTimes(): SchoolCalendarRule[] {
  return WEEKDAYS.slice(0, 5).map((day) => ({
    ...SchoolTimePlaceholder,
    id: undefined,
    version: 1,
    recurrence: {
      ...SchoolTimePlaceholder.recurrence,
      days: [day],
    },
  }));
}

function formatInput(time: string, placeholder: number) {
  const integerTime = convertToIntegerTime(time);
  if (integerTime === -1) return convertToPrintableTime(placeholder);
  return convertToPrintableTime(integerTime);
}

export default SchoolCalendar;
